/**
 * Card Settings → Promo mode.
 *
 * Which promo the card pins, plus the SAME logo and wallpaper controls Review
 * Shield uses — the same upload handlers, the same card fields
 * (review_shield_logo_url / review_shield_background_image_url). No new
 * storage and no second branding system.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Megaphone, Image as ImageIcon, X, Loader2, MousePointerClick } from 'lucide-react';

import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

export const CardPromoPanel = ({
    cardId, organizationId,
    logoUrl, setLogoUrl, backgroundUrl, setBackgroundUrl,
    onUploadLogo, onUploadWallpaper, logoInputRef, wallpaperInputRef, uploading,
    promoId: promoIdProp, setPromoId: setPromoIdProp,
    onApplied = () => {}, onSecondButtonChange = () => {},
}) => {
    const { t } = useTranslation();
    const [promos, setPromos] = useState([]);
    const [promoIdLocal, setPromoIdLocal] = useState('');
    const promoId = promoIdProp !== undefined ? promoIdProp : promoIdLocal;
    const setPromoId = setPromoIdProp || setPromoIdLocal;
    const [busy, setBusy] = useState(false);
    // Optional SECOND button (first use: "Leave a public review").
    const [sb, setSb] = useState({
        enabled: false, text: { en: '', fr: '', es: '' },
        color: '', position: 'below', url: '',
    });
    const [reviewUrl, setReviewUrl] = useState(null);
    const [sbSaved, setSbSaved] = useState(null);
    const setField = (k, v) => setSb((p) => ({ ...p, [k]: v }));
    const setText = (l, v) => setSb((p) => ({ ...p, text: { ...p.text, [l]: v } }));

    useEffect(() => {
        if (!cardId) return;
        api.get(`/promo-card-second-button/${cardId}`)
            .then((r) => {
                const d = r.data || {};
                setSb({
                    enabled: !!d.enabled,
                    text: { en: d.text?.en || '', fr: d.text?.fr || '', es: d.text?.es || '' },
                    color: d.color || '', position: d.position || 'below', url: d.url || '',
                });
                setReviewUrl(d.review_url || null);
                setSbSaved({
                    enabled: !!d.enabled,
                    text: { en: d.text?.en || '', fr: d.text?.fr || '', es: d.text?.es || '' },
                    color: d.color || '', position: d.position || 'below', url: d.url || '',
                });
            })
            .catch(() => setReviewUrl(null));
    }, [cardId]);

    // The modal's own Save Changes saves this too, through the same endpoint —
    // and `dirty` is what enables that Save when only these fields changed.
    const sbDirty = !!sbSaved && JSON.stringify(sb) !== JSON.stringify(sbSaved);
    useEffect(() => {
        onSecondButtonChange({ ...sb, dirty: sbDirty, markSaved: () => setSbSaved(sb) });
    }, [sb, sbDirty]);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!organizationId) return;
        api.get('/promos', { params: { organization_id: organizationId } })
            .then((r) => setPromos(r.data.promos || []))
            .catch(() => setPromos([]));
    }, [organizationId]);



    const apply = async () => {
        setBusy(true);
        try {
            await api.put(`/promo-card-mode/${cardId}`, {
                promo_id: promoId || null,
                logo_url: logoUrl || '',
                background_image_url: backgroundUrl || '',
                enabled: true,
                second_button_enabled: sb.enabled,
                second_button_text: sb.text,
                second_button_color: sb.color,
                second_button_position: sb.position,
                second_button_url: sb.url,
            });
            toast.success(t('cardSettings.promoApplied', 'Promo mode applied to this card'));
            onApplied(promoId || '');
            setSbSaved(sb);
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || t('cardSettings.promoApplyFailed', 'Could not apply promo mode'));
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-fuchsia-50 dark:bg-fuchsia-900/20 border border-fuchsia-200 dark:border-fuchsia-800
                        rounded-lg p-3 space-y-4" data-testid="card-promo-panel">
            <p className="text-sm font-medium text-fuchsia-800 dark:text-fuchsia-300 flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                {t('cardSettings.promoPanelTitle', 'Promo card')}
            </p>

            <div>
                <Label className="mb-1 block text-sm">
                    {t('cardSettings.whichPromo', 'Which promo')}
                </Label>
                <select value={promoId} onChange={(e) => setPromoId(e.target.value)}
                        data-testid="card-promo-select"
                        className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                    <option value="">
                        {t('cardSettings.promoUseOrgActive', "Use the organization's active promo")}
                    </option>
                    {promos.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name} — {t(`promo.status.${p.status}`, p.status)}
                        </option>
                    ))}
                </select>
            </div>

            {/* Logo — the same field and the same upload path as Review Shield */}
            <div>
                <Label className="mb-1 block text-sm">
                    <span className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        {t('reviewShield.logoLabel', 'Logo')}
                    </span>
                </Label>
                {logoUrl && (
                    <div className="mb-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border relative">
                        <img src={logoUrl} alt="" className="max-h-16 max-w-[150px] object-contain mx-auto"
                             onError={(e) => { e.target.style.display = 'none'; }} />
                        <button type="button" onClick={() => setLogoUrl('')}
                                data-testid="card-promo-clear-logo"
                                className="absolute top-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-600">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <div className="flex gap-2">
                    <input ref={logoInputRef} type="file" className="hidden"
                           accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                           onChange={onUploadLogo} data-testid="card-promo-logo-file-input" />
                    <Button type="button" variant="outline" size="sm" disabled={uploading}
                            onClick={() => logoInputRef?.current?.click()}
                            data-testid="card-promo-upload-logo-btn" className="flex-1">
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('cardSettings.upload', 'Upload')}
                    </Button>
                </div>
                <Input value={logoUrl || ''} onChange={(e) => setLogoUrl(e.target.value)}
                       placeholder="https://example.com/logo.png" className="mt-2"
                       data-testid="card-promo-logo-url-input" />
            </div>

            {/* Wallpaper — same field, same upload path */}
            <div>
                <Label className="mb-1 block text-sm">
                    <span className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        {t('reviewShield.wallpaperLabel', 'Wallpaper')}
                    </span>
                </Label>
                {backgroundUrl && (
                    <div className="mb-2 rounded-lg border overflow-hidden relative">
                        <img src={backgroundUrl} alt="" className="h-20 w-full object-cover"
                             onError={(e) => { e.target.style.display = 'none'; }} />
                        <button type="button" onClick={() => setBackgroundUrl('')}
                                data-testid="card-promo-clear-wallpaper"
                                className="absolute top-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-600">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <input ref={wallpaperInputRef} type="file" className="hidden"
                       accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                       onChange={onUploadWallpaper} data-testid="card-promo-wallpaper-file-input" />
                <Button type="button" variant="outline" size="sm" disabled={uploading}
                        onClick={() => wallpaperInputRef?.current?.click()}
                        data-testid="card-promo-upload-wallpaper-btn" className="w-full">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('cardSettings.upload', 'Upload')}
                </Button>
                <Input value={backgroundUrl || ''} onChange={(e) => setBackgroundUrl(e.target.value)}
                       placeholder="https://example.com/wallpaper.jpg" className="mt-2"
                       data-testid="card-promo-wallpaper-url-input" />
            </div>


            {/* ── Optional SECOND button ── */}
            <div className="rounded-lg border border-fuchsia-200 dark:border-fuchsia-800 p-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="promo-second-switch" className="text-sm flex items-center gap-2">
                        <MousePointerClick className="w-4 h-4" />
                        {t('cardSettings.promoSecondSwitch', 'Promo + second button (e.g. public review)')}
                    </Label>
                    <Switch id="promo-second-switch" checked={sb.enabled}
                            onCheckedChange={(v) => setField('enabled', v)}
                            data-testid="card-promo-second-switch" />
                </div>

                {sb.enabled && (
                    <div className="space-y-3">
                        <div>
                            <Label className="mb-1 block text-sm">
                                {t('cardSettings.promoSecondText', 'Button text')}
                            </Label>
                            {['en', 'fr', 'es'].map((l) => (
                                <div key={l} className="flex items-center gap-2 mb-1">
                                    <span className="w-7 text-xs uppercase text-muted-foreground">{l}</span>
                                    <Input value={sb.text[l]} onChange={(e) => setText(l, e.target.value)}
                                           placeholder={t('cardSettings.promoSecondTextDefault', 'Leave a public review')}
                                           data-testid={`card-promo-second-text-${l}`} />
                                </div>
                            ))}
                        </div>

                        <div className="flex items-end gap-3">
                            <div>
                                <Label className="mb-1 block text-sm">
                                    {t('cardSettings.promoSecondColor', 'Button colour')}
                                </Label>
                                <input type="color" value={sb.color || '#2563eb'}
                                       onChange={(e) => setField('color', e.target.value)}
                                       data-testid="card-promo-second-color"
                                       className="h-9 w-16 rounded-md border bg-background p-1" />
                            </div>
                            <div className="flex-1">
                                <Label className="mb-1 block text-sm">
                                    {t('cardSettings.promoSecondPosition', 'Position')}
                                </Label>
                                <select value={sb.position} onChange={(e) => setField('position', e.target.value)}
                                        data-testid="card-promo-second-position"
                                        className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                                    <option value="above">{t('cardSettings.promoSecondAbove', 'Above the promo button')}</option>
                                    <option value="below">{t('cardSettings.promoSecondBelow', 'Below the promo button')}</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <Label className="mb-1 block text-sm">
                                {t('cardSettings.promoSecondUrl', 'Where it goes')}
                            </Label>
                            <Input value={sb.url} onChange={(e) => setField('url', e.target.value)}
                                   placeholder={reviewUrl || 'https://…'}
                                   data-testid="card-promo-second-url" />
                            <p className="text-[11px] mt-1 text-muted-foreground"
                               data-testid="card-promo-second-url-note">
                                {reviewUrl
                                    ? t('cardSettings.promoSecondUsesReview',
                                        "Leave empty to use this card's public review link.")
                                    : t('cardSettings.promoSecondNoReview',
                                        'This card has no public review link set, so the button will only appear if you type a URL here.')}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* A small preview of what the tap will show */}
            <div>
                <Label className="mb-1 block text-sm">
                    {t('cardSettings.promoPreview', 'Preview')}
                </Label>
                <div className="rounded-xl border overflow-hidden" data-testid="card-promo-preview">
                    <div className="px-4 py-6 text-center space-y-3"
                         style={backgroundUrl ? {
                             backgroundImage: `linear-gradient(rgba(2,6,23,.72), rgba(2,6,23,.88)), url(${backgroundUrl})`,
                             backgroundSize: 'cover', backgroundPosition: 'center',
                         } : { background: '#020617' }}>
                        {logoUrl && <img src={logoUrl} alt="" className="mx-auto h-10 object-contain"
                                         onError={(e) => { e.target.style.display = 'none'; }} />}
                        <p className="text-white font-bold text-base">
                            {t('cardSettings.promoPreviewHeadline', 'Welcome to …')}
                        </p>
                        {sb.enabled && sb.position === 'above' && (
                            <div>
                                <span className="inline-block rounded-full px-4 py-2 text-sm font-semibold text-white"
                                      style={{ background: sb.color || '#2563eb' }}>
                                    {sb.text.en || t('cardSettings.promoSecondTextDefault', 'Leave a public review')}
                                </span>
                            </div>
                        )}
                        <span className="inline-block rounded-full px-4 py-2 text-sm font-semibold bg-fuchsia-500 text-white">
                            {t('promo.continue', 'Continue')}
                        </span>
                        {sb.enabled && sb.position !== 'above' && (
                            <div>
                                <span className="inline-block rounded-full px-4 py-2 text-sm font-semibold text-white"
                                      style={{ background: sb.color || '#2563eb' }}>
                                    {sb.text.en || t('cardSettings.promoSecondTextDefault', 'Leave a public review')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Button type="button" onClick={apply} disabled={busy || !cardId}
                    data-testid="card-promo-apply-btn" className="w-full">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {t('cardSettings.promoApply', 'Apply promo mode to this card')}
            </Button>
            <p className="text-[11px] text-muted-foreground">
                {t('cardSettings.promoApplyNote',
                   'The logo and wallpaper are the card\'s existing branding fields, shared with Review Shield.')}
            </p>
        </div>
    );
};
